import { SessionReport, SessionReportCoach, SessionReportResult } from './types';
import { pickCoachFromReport } from './excel';
import { sessionReportFailureReason } from './errors';

/**
 * 엑셀용으로 강사별 회원 행을 모은다. 서버 부하를 생각해 동시에 `concurrency`(기본 2)개까지만 부른다.
 * 한 강사라도 실패하면 `failed` 에 이름을 담아 돌려준다 — 호출부는 파일을 만들지 않고 누가 빠졌는지 알린다.
 */
export const isAbortError = (error: unknown) =>
    (error as { name?: string })?.name === 'AbortError' || (error as { name?: string })?.name === 'CancelledError';

export const collectCoachReports = async ({
    coaches,
    fetchCoach,
    concurrency = 2,
    onProgress,
    signal,
}: {
    coaches: Pick<SessionReportCoach, 'coachId' | 'coachName'>[];
    fetchCoach: (coachId: string) => Promise<SessionReportResult>;
    concurrency?: number;
    onProgress?: (done: number, total: number) => void;
    /** 취소되면 새 요청을 시작하지 않고, 취소 뒤에 끝난 실패는 기록하지 않는다 */
    signal?: AbortSignal;
}): Promise<{ reports: Record<string, SessionReport>; failed: { coachName: string; reason: string }[] }> => {
    const reports: Record<string, SessionReport> = {};
    const failed: { coachName: string; reason: string }[] = [];
    let next = 0;
    let done = 0;
    onProgress?.(0, coaches.length);

    const worker = async () => {
        while (next < coaches.length && !signal?.aborted) {
            const coach = coaches[next];
            next += 1;
            try {
                const result = await fetchCoach(coach.coachId);
                if (signal?.aborted) return;
                if (result.status === 'ok' && pickCoachFromReport(result.report, coach.coachId)) {
                    reports[coach.coachId] = result.report;
                } else {
                    failed.push({ coachName: coach.coachName, reason: result.status === 'ok' ? '회원 자료 없음' : '조회 실패' });
                }
            } catch (error) {
                if (signal?.aborted || isAbortError(error)) return;
                failed.push({ coachName: coach.coachName, reason: sessionReportFailureReason(error) });
            }
            done += 1;
            if (!signal?.aborted) onProgress?.(done, coaches.length);
        }
    };

    await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, coaches.length)) }, worker));
    // 강사 순서는 총괄 순서를 따른다(완료 순서가 아니라).
    const order = new Map(coaches.map((c, i) => [c.coachName, i]));
    failed.sort((a, b) => (order.get(a.coachName) ?? 0) - (order.get(b.coachName) ?? 0));
    return { reports, failed };
};

// ───────────────────────── 엑셀: 같은 시점의 값인지 대조 ─────────────────────────

/**
 * 키 순서와 무관한 정규 문자열. 서버는 플래그 맵(checkFlagCounts 등)의 키를 행을 읽는 순서대로 넣어서
 * 같은 값이라도 요약 조회와 강사 조회의 키 순서가 다를 수 있다. 객체는 키를 정렬하고,
 * 배열은 `orderedArrays` 에 든 경로만 순서를 지키고 나머지(인계 상대 목록 등 순서에 뜻이 없는 것)는 정렬한다.
 */
export const canonicalize = (value: unknown, orderedArrays: ReadonlySet<string> = new Set(), path = ''): string => {
    if (value === undefined) return 'undefined';
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
        const items = value.map((v) => canonicalize(v, orderedArrays, `${path}[]`));
        if (!orderedArrays.has(path)) items.sort();
        return `[${items.join(',')}]`;
    }
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .sort()
        .map((k) => `${JSON.stringify(k)}:${canonicalize(obj[k], orderedArrays, path ? `${path}.${k}` : k)}`)
        .join(',')}}`;
};

/**
 * 강사 요약에서 대조할 값. 판매담당자 기준 금액은 강사 조회에선 null 이라(계약 §0-4) 뺀다.
 * 시트 합계(totals)도 함께 본다 — 요약 조회에도 rows 없이 totals 는 온다.
 */
const coachFingerprint = (coach: SessionReportCoach) => {
    const { salesManagerBasisAmount: _omit, ...reference } = coach.summary.reference;
    return canonicalize({
        coachId: coach.coachId,
        coachName: coach.coachName,
        workRoutineMissing: coach.workRoutineMissing,
        summary: { ...coach.summary, reference },
        totals: {
            valid: coach.sheets.valid?.totals,
            expired: coach.sheets.expired?.totals,
            ghost: coach.sheets.ghost?.totals,
        },
    });
};

/** 같은 강사의 요약 행과 강사 조회 응답이 같은 계산 결과인지(키 순서·판매담당자 기준 금액 무시). */
export const isSameCoachSnapshot = (a: SessionReportCoach, b: SessionReportCoach) =>
    coachFingerprint(a) === coachFingerprint(b);

const sameBasis = (report: SessionReport, yearMonth: string, ghostDays: number) =>
    report.yearMonth === yearMonth && report.ghostDays === ghostDays;

/** 처음 요약·끝 요약·강사별 응답이 모두 같은 값인지. 하나라도 다르면 false. */
export const isConsistentExport = ({
    first,
    last,
    reports,
    yearMonth,
    ghostDays,
}: {
    first: SessionReport;
    last: SessionReport;
    reports: Record<string, SessionReport>;
    yearMonth: string;
    ghostDays: number;
}) => {
    if (!sameBasis(first, yearMonth, ghostDays) || !sameBasis(last, yearMonth, ghostDays)) return false;
    if (first.coaches.length !== last.coaches.length) return false;
    if (canonicalize(first.centerTotals) !== canonicalize(last.centerTotals)) return false;
    return first.coaches.every((c, i) => {
        const end = last.coaches[i];
        const report = reports[c.coachId];
        const detail = report ? pickCoachFromReport(report, c.coachId) : undefined;
        if (!end || !report || !detail || !sameBasis(report, yearMonth, ghostDays)) return false;
        const fp = coachFingerprint(c);
        return fp === coachFingerprint(end) && fp === coachFingerprint(detail);
    });
};

export type ConsistentExportResult =
    | { status: 'ok'; summary: SessionReport; reports: Record<string, SessionReport> }
    /** 요약을 받지 못함(시간 초과·오류·구서버) */
    | { status: 'summaryFailed'; reason: string }
    | { status: 'coachFailed'; failed: { coachName: string; reason: string }[] }
    /** 받는 사이 데이터가 바뀜(재시도 후에도) */
    | { status: 'changed' }
    /** 화면을 떠나거나 로그아웃해 취소됨 — 알림을 띄우지 않는다 */
    | { status: 'aborted' };

/**
 * 엑셀용 수집. (1) 요약을 새로 받고 (2) 같은 yearMonth·ghostDays 로 강사별을 받고 (3) 끝에 요약을 한 번 더 받는다.
 * 각 강사의 요약이 처음·끝·강사 응답에서 모두 같아야 파일을 만든다. 다르면 처음부터 1회 다시,
 * 그래도 다르면 `changed`. 총괄 시트는 대조를 통과한 그 요약으로 만든다.
 * fetch 함수는 캐시를 쓰지 않고 실제로 요청해야 한다.
 */
export const collectConsistentExport = async ({
    yearMonth,
    ghostDays,
    fetchSummary,
    fetchCoach,
    concurrency = 2,
    onProgress,
    maxAttempts = 2,
    signal,
}: {
    yearMonth: string;
    ghostDays: number;
    fetchSummary: () => Promise<SessionReportResult>;
    fetchCoach: (coachId: string) => Promise<SessionReportResult>;
    concurrency?: number;
    onProgress?: (done: number, total: number) => void;
    maxAttempts?: number;
    signal?: AbortSignal;
}): Promise<ConsistentExportResult> => {
    const aborted = (): ConsistentExportResult | null => (signal?.aborted ? { status: 'aborted' } : null);
    const getSummary = async (): Promise<SessionReport | string> => {
        try {
            const r = await fetchSummary();
            if (r.status !== 'ok') return r.status === 'unavailable' ? '서버 미지원' : '아직 시작하지 않은 달';
            return r.report;
        } catch (error) {
            if (signal?.aborted || isAbortError(error)) return ABORTED;
            return sessionReportFailureReason(error);
        }
    };

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (aborted()) return { status: 'aborted' };
        const first = await getSummary();
        if (aborted() || first === ABORTED) return { status: 'aborted' };
        if (typeof first === 'string') return { status: 'summaryFailed', reason: first };
        const { reports, failed } = await collectCoachReports({
            coaches: first.coaches,
            fetchCoach,
            concurrency,
            onProgress,
            signal,
        });
        if (aborted()) return { status: 'aborted' };
        if (failed.length > 0) return { status: 'coachFailed', failed };
        const last = await getSummary();
        if (aborted() || last === ABORTED) return { status: 'aborted' };
        if (typeof last === 'string') return { status: 'summaryFailed', reason: last };
        if (isConsistentExport({ first, last, reports, yearMonth, ghostDays })) {
            return { status: 'ok', summary: first, reports };
        }
    }
    return aborted() ?? { status: 'changed' };
};

const ABORTED = '__aborted__';

// ───────────────────────── 엑셀: 다 모은 뒤 저장해도 되는지 ─────────────────────────

/** 로그인 주체(센터 + 관리자). 읽기 전용 수집이라 같은 주체의 토큰 갱신은 그대로 둔다. */
export const exportPrincipalKey = (authObject: { centerId?: string; adminId?: string } | null | undefined) =>
    authObject?.centerId && authObject?.adminId ? `${authObject.centerId}|${authObject.adminId}` : null;

/** 화면을 떠나서 취소된 경우(알림 없음)와 로그인 주체가 바뀐 경우(알림)를 가른다. */
export const PRINCIPAL_CHANGED_ABORT = 'principal-changed';

/**
 * - `silent`: 사용자가 화면을 떠났다(언마운트·다른 메뉴·로그아웃). 파일도 알림도 없다.
 * - `principalChanged`: 화면엔 있지만 로그인 주체·센터가 바뀌었다. 파일을 버리고 그 사실만 알린다(이전 강사명 없이).
 * - `proceed`: 같은 주체 — 토큰이 갱신됐어도 결과를 쓴다.
 */
export const decideExportOutcome = ({
    abortReason,
    aborted,
    active,
    startPrincipal,
    currentPrincipal,
}: {
    aborted: boolean;
    abortReason?: unknown;
    active: boolean;
    startPrincipal: string | null;
    currentPrincipal: string | null;
}): 'silent' | 'principalChanged' | 'proceed' => {
    if (!active) return 'silent';
    if (aborted) return abortReason === PRINCIPAL_CHANGED_ABORT ? 'principalChanged' : 'silent';
    if (!startPrincipal || startPrincipal !== currentPrincipal) return 'principalChanged';
    return 'proceed';
};
